/**
 * Canonical template catalog — single source of truth.
 *
 * EVERY template the runtime understands must be listed here.
 * Both the flow-schema validator and the plan validator import this.
 * When a new template is added to the runtime registry, add it here too.
 *
 * Categories are kept for planner context: the AI uses them to pick
 * appropriate visuals for a given topic.
 */

// ─── Template → allowed actions mapping ──────────────────────────────

export const TEMPLATE_CATALOG: Record<string, readonly string[]> = {
  // Core / Foundational
  box_flow: ["draw", "highlight", "fade", "zoom", "label", "arrow", "animate_flow"],
  branching_tube: ["draw", "highlight", "fade", "zoom", "animate_flow"],
  cylinder: ["draw", "move", "highlight", "fade", "zoom"],
  highlight_overlay: ["draw", "fade_in", "fade_out", "pulse", "highlight"],
  zoom_frame: ["zoom_in", "zoom_out", "move"],
  label_anchor: ["draw", "attach", "fade"],
  timeline_marker: ["highlight", "advance", "fade_previous"],
  guide_actor: ["enter", "exit", "point", "react"],

  // Process Diagrams
  circular_cycle: ["draw", "highlight", "advance", "fade"],
  funnel_diagram: ["draw", "highlight", "fade"],
  linear_pipeline: ["draw", "highlight", "fade", "animate_flow"],
  waterfall_steps: ["draw", "highlight", "fade"],
  spiral_path: ["draw", "highlight", "advance", "fade"],
  parallel_process: ["draw", "highlight", "fade", "animate_flow"],

  // Structure Diagrams
  pyramid_layers: ["draw", "highlight", "fade"],
  tree_diagram: ["draw", "highlight", "fade"],
  concentric_rings: ["draw", "highlight", "advance", "fade"],
  hex_grid: ["draw", "highlight", "fade"],
  radial_burst: ["draw", "highlight", "fade"],
  accordion: ["draw", "highlight", "advance", "fade"],

  // Comparison Diagrams
  venn_diagram: ["draw", "highlight", "fade"],
  balance_scale: ["draw", "highlight", "fade"],
  matrix_grid: ["draw", "highlight", "fade"],
  stacked_bars: ["draw", "highlight", "fade"],
  pie_segments: ["draw", "highlight", "fade"],

  // Science Diagrams
  cell_diagram: ["draw", "highlight", "zoom", "fade"],
  atom_orbital: ["draw", "highlight", "fade"],
  dna_helix: ["draw", "highlight", "fade"],
  layered_cross: ["draw", "highlight", "fade"],
  wave_pattern: ["draw", "highlight", "fade"],
  heart_chambers: ["draw", "highlight", "fade"],
  solar_system: ["draw", "highlight", "fade"],

  // Tech Diagrams
  network_graph: ["draw", "highlight", "fade"],
  circuit_board: ["draw", "highlight", "fade"],
  neural_network: ["draw", "highlight", "fade"],
  block_chain: ["draw", "highlight", "fade"],
  server_rack: ["draw", "highlight", "fade"],
  flow_chart: ["draw", "highlight", "fade"],

  // Measurement Diagrams
  gauge_meter: ["draw", "highlight", "fade"],
  thermometer_bar: ["draw", "highlight", "fade"],
  microscope_view: ["draw", "highlight", "fade"],
  chemical_equation: ["draw", "highlight", "fade"],

  // Geography Diagrams
  world_map: ["draw", "highlight", "fade"],
  rotating_globe: ["draw", "highlight", "fade"],
  continent_outline: ["draw", "highlight", "fade"],
  compass_rose: ["draw", "highlight", "fade"],
  topographic_map: ["draw", "highlight", "fade"],
  route_path: ["draw", "highlight", "fade", "animate_flow"],
  city_grid: ["draw", "highlight", "fade"],
  terrain_cross_section: ["draw", "highlight", "fade"],
  ocean_current: ["draw", "highlight", "fade", "animate_flow"],
  climate_zone: ["draw", "highlight", "fade"],

  // Symbol Icons
  lightning_bolt: ["draw", "highlight", "fade"],
  light_bulb: ["draw", "highlight", "fade"],
  idea_bulb: ["draw", "highlight", "fade"],
  science_bulb: ["draw", "highlight", "fade"],
  gear_mechanism: ["draw", "highlight", "fade"],
  arrow_cycle: ["draw", "highlight", "advance", "fade"],
  people_group: ["draw", "highlight", "fade"],
  clock_face: ["draw", "highlight", "fade"],
  shield_badge: ["draw", "highlight", "fade"],
  magnifying_glass: ["draw", "highlight", "fade"],
  target_bullseye: ["draw", "highlight", "fade"],
  key_lock: ["draw", "highlight", "fade"],
  flag_banner: ["draw", "highlight", "fade"],
  trophy_cup: ["draw", "highlight", "fade"],

  // Human Body Diagrams
  brain_diagram: ["draw", "highlight", "zoom", "fade"],
  eye_anatomy: ["draw", "highlight", "zoom", "fade"],
  ear_anatomy: ["draw", "highlight", "fade"],
  skeleton_system: ["draw", "highlight", "fade"],
  digestive_tract: ["draw", "highlight", "advance", "fade"],
  muscle_fiber: ["draw", "highlight", "fade"],
  tooth_cross_section: ["draw", "highlight", "fade"],
  skin_layers: ["draw", "highlight", "fade"],

  // Space Diagrams
  rocket_launch: ["draw", "highlight", "fade"],
  satellite_orbit: ["draw", "highlight", "fade"],
  galaxy_spiral: ["draw", "highlight", "fade"],
  moon_phases: ["draw", "highlight", "advance", "fade"],
  constellation_map: ["draw", "highlight", "fade"],
  star_lifecycle: ["draw", "highlight", "advance", "fade"],
  telescope_view: ["draw", "highlight", "fade"],
  asteroid_belt: ["draw", "highlight", "fade"],

  // Math Diagrams
  coordinate_plane: ["draw", "highlight", "fade"],
  geometric_shapes: ["draw", "highlight", "fade"],
  number_line: ["draw", "highlight", "fade"],
  bar_chart: ["draw", "highlight", "fade"],
  line_graph: ["draw", "highlight", "fade"],
  fraction_visual: ["draw", "highlight", "fade"],
  angle_protractor: ["draw", "highlight", "fade"],
  probability_tree: ["draw", "highlight", "fade"],

  // Nature Diagrams
  mountain_landscape: ["draw", "highlight", "fade"],
  volcano_cross_section: ["draw", "highlight", "fade"],
  ocean_layers: ["draw", "highlight", "advance", "fade"],
  ecosystem_web: ["draw", "highlight", "fade"],
  river_delta: ["draw", "highlight", "fade", "animate_flow"],
  rock_strata: ["draw", "highlight", "advance", "fade"],
  weather_system: ["draw", "highlight", "advance", "fade"],
  forest_canopy: ["draw", "highlight", "advance", "fade"],

  // Physics Diagrams
  gear_train: ["draw", "highlight", "fade"],
  pulley_system: ["draw", "highlight", "fade"],
  pendulum_swing: ["draw", "highlight", "fade"],
  magnet_field: ["draw", "highlight", "fade"],
  lens_optics: ["draw", "highlight", "fade"],
  spring_force: ["draw", "highlight", "fade"],
  inclined_plane: ["draw", "highlight", "fade"],
  wave_interference: ["draw", "highlight", "fade"],

  // Business Diagrams
  revenue_chart: ["draw", "highlight", "fade"],
  supply_demand: ["draw", "highlight", "fade"],
  org_hierarchy: ["draw", "highlight", "fade"],
  calendar_schedule: ["draw", "highlight", "fade"],
  kanban_board: ["draw", "highlight", "fade"],
  swot_matrix: ["draw", "highlight", "fade"],
  market_share: ["draw", "highlight", "fade"],
  growth_curve: ["draw", "highlight", "fade"],

  // History & Everyday
  era_timeline: ["draw", "highlight", "advance", "fade"],
  castle_fortress: ["draw", "highlight", "fade"],
  ancient_monument: ["draw", "highlight", "fade"],
  trade_route: ["draw", "highlight", "fade", "animate_flow"],
  food_pyramid_diagram: ["draw", "highlight", "advance", "fade"],
  water_cycle_diagram: ["draw", "highlight", "advance", "fade"],
  traffic_light: ["draw", "highlight", "advance", "fade"],
  house_blueprint: ["draw", "highlight", "fade"],
  sports_field: ["draw", "highlight", "fade"],
  color_spectrum: ["draw", "highlight", "fade"],

  // Animal Diagrams
  butterfly_lifecycle: ["draw", "highlight", "advance", "fade"],
  fish_anatomy: ["draw", "highlight", "zoom", "fade"],
  dinosaur_skeleton: ["draw", "highlight", "fade"],
  bird_flight: ["draw", "highlight", "fade"],
  whale_dive: ["draw", "highlight", "fade"],
  ant_colony: ["draw", "highlight", "advance", "fade"],
  frog_metamorphosis: ["draw", "highlight", "advance", "fade"],
  spider_web: ["draw", "highlight", "fade"],

  // Energy Diagrams
  solar_panel: ["draw", "highlight", "fade"],
  wind_turbine: ["draw", "highlight", "fade"],
  battery_cell: ["draw", "highlight", "fade"],
  nuclear_reactor: ["draw", "highlight", "fade", "animate_flow"],
  hydroelectric_dam: ["draw", "highlight", "fade", "animate_flow"],
  power_grid_diagram: ["draw", "highlight", "fade", "animate_flow"],
  flame_fire: ["draw", "highlight", "advance", "fade"],
  lightning_storm: ["draw", "highlight", "fade"],

  // Transport Diagrams
  airplane_cross: ["draw", "highlight", "fade"],
  car_dashboard: ["draw", "highlight", "fade"],
  bicycle_mechanics: ["draw", "highlight", "fade"],
  train_system: ["draw", "highlight", "fade"],
  submarine_dive: ["draw", "highlight", "fade"],
  helicopter_rotor: ["draw", "highlight", "fade"],
  sailboat_wind: ["draw", "highlight", "fade"],
  hot_air_balloon: ["draw", "highlight", "fade"],

  // Chemistry Diagrams
  periodic_table_view: ["draw", "highlight", "advance", "fade"],
  molecule_3d: ["draw", "highlight", "fade"],
  chemical_bond: ["draw", "highlight", "advance", "fade"],
  ph_scale: ["draw", "highlight", "advance", "fade"],
  crystal_lattice: ["draw", "highlight", "fade"],
  gas_particles: ["draw", "highlight", "fade"],
  distillation_setup: ["draw", "highlight", "fade", "animate_flow"],
  electrochemistry_cell: ["draw", "highlight", "fade", "animate_flow"],

  // Plant Biology Diagrams
  flower_bloom: ["draw", "highlight", "fade"],
  tree_growth_rings: ["draw", "highlight", "advance", "fade"],
  root_network: ["draw", "highlight", "fade"],
  seed_sprout: ["draw", "highlight", "advance", "fade"],
  leaf_photosynthesis: ["draw", "highlight", "fade", "animate_flow"],
  pollen_drift: ["draw", "highlight", "fade"],
  fruit_stages: ["draw", "highlight", "advance", "fade"],
  moss_spores: ["draw", "highlight", "fade"],

  // Architecture Diagrams
  bridge_forces: ["draw", "highlight", "advance", "fade"],
  skyscraper_sway: ["draw", "highlight", "fade"],
  arch_physics: ["draw", "highlight", "advance", "fade"],
  dome_pressure: ["draw", "highlight", "fade"],
  pyramid_build: ["draw", "highlight", "fade"],
  aqueduct_flow: ["draw", "highlight", "fade", "animate_flow"],
  windmill_spin: ["draw", "highlight", "fade"],
  lighthouse_beam: ["draw", "highlight", "fade"],

  // People & Characters
  family_group: ["draw", "highlight", "advance", "fade"],
  team_circle: ["draw", "highlight", "advance", "fade"],
  single_girl: ["draw", "highlight", "fade"],
  single_boy: ["draw", "highlight", "fade"],
  teacher_student: ["draw", "highlight", "advance", "fade"],
  handshake_deal: ["draw", "highlight", "fade"],
  crowd_scene: ["draw", "highlight", "fade"],
  couple_love: ["draw", "highlight", "fade"],

  // Emotions & Concepts
  idea_lightbulb: ["draw", "highlight", "fade"],
  thumbs_up: ["draw", "highlight", "fade"],
  question_doubt: ["draw", "highlight", "fade"],
  happy_smiley: ["draw", "highlight", "fade"],
  love_hearts: ["draw", "highlight", "fade"],
  celebration_confetti: ["draw", "highlight", "fade"],
  thinking_cloud: ["draw", "highlight", "fade"],
  surprise_wow: ["draw", "highlight", "fade"],

  // Cosmic Universe
  black_hole: ["draw", "highlight", "advance", "fade"],
  nebula_cloud: ["draw", "highlight", "fade"],
  supernova_explosion: ["draw", "highlight", "fade"],
  comet_trail: ["draw", "highlight", "fade"],
  big_bang: ["draw", "highlight", "advance", "fade"],
  exoplanet_transit: ["draw", "highlight", "fade"],
  pulsar_spin: ["draw", "highlight", "fade"],
  dark_matter_web: ["draw", "highlight", "fade"],

  // Flat Storyboards
  story_panel: ["draw", "highlight", "advance", "fade"],
  dialogue_bubble: ["draw", "highlight", "fade"],
  character_walk_cycle: ["draw", "highlight", "fade"],
  narrative_arc: ["draw", "highlight", "advance", "fade"],
  split_screen_compare: ["draw", "highlight", "fade"],
  book_page_turn: ["draw", "highlight", "advance", "fade"],
  montage_grid: ["draw", "highlight", "advance", "fade"],
  infographic_timeline: ["draw", "highlight", "advance", "fade"],

  // Daily Life Scenes
  kitchen_scene: ["draw", "highlight", "advance", "fade"],
  classroom_scene: ["draw", "highlight", "advance", "fade"],
  office_desk: ["draw", "highlight", "fade"],
  playground: ["draw", "highlight", "fade"],
  park_bench: ["draw", "highlight", "fade"],
  shopping_cart: ["draw", "highlight", "advance", "fade"],
  dining_table: ["draw", "highlight", "advance", "fade"],
  bedroom_scene: ["draw", "highlight", "fade"],

  // Puzzles & Games
  jigsaw_puzzle: ["draw", "highlight", "advance", "fade"],
  maze_path: ["draw", "highlight", "fade"],
  chess_board: ["draw", "highlight", "advance", "fade"],
  dice_roll: ["draw", "highlight", "fade"],
  card_deck: ["draw", "highlight", "advance", "fade"],
  rubik_cube: ["draw", "highlight", "fade"],
  board_game: ["draw", "highlight", "advance", "fade"],
  word_search: ["draw", "highlight", "advance", "fade"],

  // Vehicle Animations
  car_driving: ["draw", "highlight", "fade"],
  truck_hauling: ["draw", "highlight", "fade"],
  motorcycle_lean: ["draw", "highlight", "fade"],
  bus_route: ["draw", "highlight", "advance", "fade"],
  rocket_launch_detailed: ["draw", "highlight", "advance", "fade"],
  spaceship_warp: ["draw", "highlight", "fade"],
  yacht_sailing: ["draw", "highlight", "fade"],
  bicycle_ride: ["draw", "highlight", "fade"],

  // Gravity & Forces
  gravity_well: ["draw", "highlight", "fade"],
  projectile_arc: ["draw", "highlight", "fade"],
  orbital_mechanics: ["draw", "highlight", "fade"],
  tidal_forces: ["draw", "highlight", "fade"],
  magnetic_field_lines: ["draw", "highlight", "fade"],
  electromagnetic_wave: ["draw", "highlight", "fade"],
  centripetal_force: ["draw", "highlight", "fade"],
  free_fall: ["draw", "highlight", "fade"],

  // World & Culture
  globe_with_pins: ["draw", "highlight", "fade"],
  world_map_animated: ["draw", "highlight", "advance", "fade"],
  cultural_symbols: ["draw", "highlight", "advance", "fade"],
  music_instruments: ["draw", "highlight", "advance", "fade"],
  traditional_dance: ["draw", "highlight", "fade"],
  festival_lanterns: ["draw", "highlight", "fade"],
  language_bubbles: ["draw", "highlight", "advance", "fade"],
  food_world: ["draw", "highlight", "advance", "fade"],

  // Micro Biology
  cell_division: ["draw", "highlight", "advance", "fade"],
  virus_structure: ["draw", "highlight", "advance", "fade"],
  bacteria_colony: ["draw", "highlight", "fade"],
  immune_response: ["draw", "highlight", "advance", "fade"],
  neuron_firing: ["draw", "highlight", "fade"],
  dna_replication: ["draw", "highlight", "fade"],
  protein_folding: ["draw", "highlight", "fade"],
  blood_cells: ["draw", "highlight", "advance", "fade"],

  // Flat Characters 2D
  walking_character: ["draw", "highlight", "fade"],
  running_character: ["draw", "highlight", "fade"],
  jumping_character: ["draw", "highlight", "fade"],
  waving_character: ["draw", "highlight", "fade"],
  sitting_reading: ["draw", "highlight", "fade"],
  pointing_teacher: ["draw", "highlight", "fade"],
  dancing_character: ["draw", "highlight", "fade"],
  swimming_character: ["draw", "highlight", "fade"],

  // Abstract Concepts
  time_flow: ["draw", "highlight", "fade"],
  cause_effect: ["draw", "highlight", "advance", "fade"],
  pros_cons: ["draw", "highlight", "advance", "fade"],
  growth_chart: ["draw", "highlight", "fade"],
  teamwork_gears: ["draw", "highlight", "fade"],
  leadership_pyramid: ["draw", "highlight", "advance", "fade"],
  innovation_spark: ["draw", "highlight", "fade"],
  life_cycle: ["draw", "highlight", "advance", "fade"],

  // Concept-acting scenes (Anivaze v2 — act the idea out, not labelled boxes)
  car_speed_scene: ["draw", "highlight", "animate_flow", "fade"],
  speed_compare_scene: ["draw", "highlight", "animate_flow", "fade"],
  gravity_scene: ["draw", "highlight", "animate_flow", "fade"],
  states_of_matter_scene: ["draw", "highlight", "animate_flow", "fade"],
  water_cycle_scene: ["draw", "highlight", "animate_flow", "fade"],
  day_night_scene: ["draw", "highlight", "animate_flow", "fade"],
  photosynthesis_scene: ["draw", "highlight", "animate_flow", "fade"],
  food_chain_scene: ["draw", "highlight", "animate_flow", "fade"],
  fractions_scene: ["draw", "highlight", "animate_flow", "fade"],
  counting_scene: ["draw", "highlight", "animate_flow", "fade"],
  addition_scene: ["draw", "highlight", "animate_flow", "fade"],
  subtraction_scene: ["draw", "highlight", "animate_flow", "fade"],
  breathing_scene: ["draw", "highlight", "animate_flow", "fade"],
  electricity_scene: ["draw", "highlight", "animate_flow", "fade"],
  seasons_scene: ["draw", "highlight", "animate_flow", "fade"],
  butterfly_life_scene: ["draw", "highlight", "animate_flow", "fade"],
  rainbow_scene: ["draw", "highlight", "animate_flow", "fade"],
  volcano_scene: ["draw", "highlight", "animate_flow", "fade"],
  shapes_scene: ["draw", "highlight", "animate_flow", "fade"],
  telling_time_scene: ["draw", "highlight", "animate_flow", "fade"],
  float_sink_scene: ["draw", "highlight", "animate_flow", "fade"],
  magnet_scene: ["draw", "highlight", "animate_flow", "fade"],
  plant_grow_scene: ["draw", "highlight", "animate_flow", "fade"],
  frog_life_scene: ["draw", "highlight", "animate_flow", "fade"],
  coding_sequence_scene: ["draw", "highlight", "animate_flow", "fade"],
  coding_loop_scene: ["draw", "highlight", "animate_flow", "fade"],
  coding_if_scene: ["draw", "highlight", "animate_flow", "fade"],
  debugging_scene: ["draw", "highlight", "animate_flow", "fade"],
  machine_learning_scene: ["draw", "highlight", "animate_flow", "fade"],
  neural_network_scene: ["draw", "highlight", "animate_flow", "fade"],
  algorithm_scene: ["draw", "highlight", "animate_flow", "fade"],
  sorting_scene: ["draw", "highlight", "animate_flow", "fade"],
  journey_scene: ["draw", "highlight", "animate_flow", "fade"],
  xiaohei_idea_scene: ["draw", "highlight", "animate_flow", "fade"],
  xiaohei_walk_scene: ["draw", "highlight", "animate_flow", "fade"],
  skin_demo_scene: ["draw", "highlight", "animate_flow", "fade"],
  skin_landscape_scene: ["draw", "highlight", "animate_flow", "fade"],
  actor_gallery_scene: ["draw", "highlight", "animate_flow", "fade"],
  skin_park_scene: ["draw", "highlight", "animate_flow", "fade"],
  skin_counting_scene: ["draw", "highlight", "animate_flow", "fade"],
  skin_water_cycle: ["draw", "highlight", "animate_flow", "fade"],
  skin_day_night: ["draw", "highlight", "animate_flow", "fade"],
  skin_butterfly_life: ["draw", "highlight", "animate_flow", "fade"],
  skin_frog_life: ["draw", "highlight", "animate_flow", "fade"],
  skin_photosynthesis: ["draw", "highlight", "animate_flow", "fade"],
  skin_food_chain: ["draw", "highlight", "animate_flow", "fade"],
  skin_gravity: ["draw", "highlight", "animate_flow", "fade"],
  skin_seasons: ["draw", "highlight", "animate_flow", "fade"],
  skin_title: ["draw", "highlight", "animate_flow", "fade"],
  skin_recap: ["draw", "highlight", "animate_flow", "fade"],
  skin_breathing: ["draw", "highlight", "animate_flow", "fade"],
  skin_explainer: ["draw", "highlight", "animate_flow", "fade"],

  // Low-level primitives (internal use)
  _box: ["highlight", "fade", "zoom", "label"],
  _label: [],
  _arrow: ["arrow"],
  _highlight_overlay: ["highlight"],
} as const;

// ─── Derived helpers ─────────────────────────────────────────────────

/** Set of all template IDs the runtime understands. */
export const ALLOWED_TEMPLATE_IDS: ReadonlySet<string> = new Set(Object.keys(TEMPLATE_CATALOG));

/** Template IDs usable by the AI planner (excludes _ prefixed primitives and guide_actor). */
export const PLANNER_TEMPLATE_IDS: readonly string[] = Object.keys(TEMPLATE_CATALOG).filter(
  (id) => !id.startsWith("_") && id !== "guide_actor" && id !== "highlight_overlay" && id !== "zoom_frame" && id !== "label_anchor" && id !== "timeline_marker",
);

/** Get allowed actions for a template, or undefined if unknown. */
export function getAllowedActions(templateId: string): readonly string[] | undefined {
  return TEMPLATE_CATALOG[templateId];
}

/**
 * Template categories for planner context — helps the AI pick visuals.
 * Key = category name, value = array of template IDs in that category.
 */
export const TEMPLATE_CATEGORIES: Record<string, readonly string[]> = {
  process: ["circular_cycle", "funnel_diagram", "linear_pipeline", "waterfall_steps", "spiral_path", "parallel_process"],
  structure: ["pyramid_layers", "tree_diagram", "concentric_rings", "hex_grid", "radial_burst", "accordion"],
  comparison: ["venn_diagram", "balance_scale", "matrix_grid", "stacked_bars", "pie_segments", "split_screen_compare", "pros_cons"],
  science: ["cell_diagram", "atom_orbital", "dna_helix", "layered_cross", "wave_pattern", "heart_chambers", "solar_system"],
  tech: ["network_graph", "circuit_board", "neural_network", "block_chain", "server_rack", "flow_chart"],
  measurement: ["gauge_meter", "thermometer_bar", "microscope_view", "chemical_equation"],
  geography: ["world_map", "rotating_globe", "continent_outline", "compass_rose", "topographic_map", "route_path", "city_grid", "terrain_cross_section", "ocean_current", "climate_zone", "globe_with_pins", "world_map_animated"],
  symbol: ["lightning_bolt", "light_bulb", "idea_bulb", "science_bulb", "gear_mechanism", "arrow_cycle", "people_group", "clock_face", "shield_badge", "magnifying_glass", "target_bullseye", "key_lock", "flag_banner", "trophy_cup"],
  human_body: ["brain_diagram", "eye_anatomy", "ear_anatomy", "skeleton_system", "digestive_tract", "muscle_fiber", "tooth_cross_section", "skin_layers"],
  space: ["rocket_launch", "satellite_orbit", "galaxy_spiral", "moon_phases", "constellation_map", "star_lifecycle", "telescope_view", "asteroid_belt"],
  math: ["coordinate_plane", "geometric_shapes", "number_line", "bar_chart", "line_graph", "fraction_visual", "angle_protractor", "probability_tree"],
  nature: ["mountain_landscape", "volcano_cross_section", "ocean_layers", "ecosystem_web", "river_delta", "rock_strata", "weather_system", "forest_canopy"],
  physics: ["gear_train", "pulley_system", "pendulum_swing", "magnet_field", "lens_optics", "spring_force", "inclined_plane", "wave_interference", "gravity_well", "projectile_arc", "orbital_mechanics", "tidal_forces", "magnetic_field_lines", "electromagnetic_wave", "centripetal_force", "free_fall"],
  business: ["revenue_chart", "supply_demand", "org_hierarchy", "calendar_schedule", "kanban_board", "swot_matrix", "market_share", "growth_curve"],
  history: ["era_timeline", "castle_fortress", "ancient_monument", "trade_route"],
  everyday: ["food_pyramid_diagram", "water_cycle_diagram", "traffic_light", "house_blueprint", "sports_field", "color_spectrum"],
  animal: ["butterfly_lifecycle", "fish_anatomy", "dinosaur_skeleton", "bird_flight", "whale_dive", "ant_colony", "frog_metamorphosis", "spider_web"],
  energy: ["solar_panel", "wind_turbine", "battery_cell", "nuclear_reactor", "hydroelectric_dam", "power_grid_diagram", "flame_fire", "lightning_storm"],
  transport: ["airplane_cross", "car_dashboard", "bicycle_mechanics", "train_system", "submarine_dive", "helicopter_rotor", "sailboat_wind", "hot_air_balloon", "car_driving", "truck_hauling", "motorcycle_lean", "bus_route", "rocket_launch_detailed", "spaceship_warp", "yacht_sailing", "bicycle_ride"],
  chemistry: ["periodic_table_view", "molecule_3d", "chemical_bond", "ph_scale", "crystal_lattice", "gas_particles", "distillation_setup", "electrochemistry_cell"],
  plant_biology: ["flower_bloom", "tree_growth_rings", "root_network", "seed_sprout", "leaf_photosynthesis", "pollen_drift", "fruit_stages", "moss_spores"],
  architecture: ["bridge_forces", "skyscraper_sway", "arch_physics", "dome_pressure", "pyramid_build", "aqueduct_flow", "windmill_spin", "lighthouse_beam"],
  people: ["family_group", "team_circle", "single_girl", "single_boy", "teacher_student", "handshake_deal", "crowd_scene", "couple_love"],
  emotions: ["idea_lightbulb", "thumbs_up", "question_doubt", "happy_smiley", "love_hearts", "celebration_confetti", "thinking_cloud", "surprise_wow"],
  cosmic: ["black_hole", "nebula_cloud", "supernova_explosion", "comet_trail", "big_bang", "exoplanet_transit", "pulsar_spin", "dark_matter_web"],
  storyboard: ["story_panel", "dialogue_bubble", "character_walk_cycle", "narrative_arc", "book_page_turn", "montage_grid", "infographic_timeline"],
  daily_scene: ["kitchen_scene", "classroom_scene", "office_desk", "playground", "park_bench", "shopping_cart", "dining_table", "bedroom_scene"],
  puzzle: ["jigsaw_puzzle", "maze_path", "chess_board", "dice_roll", "card_deck", "rubik_cube", "board_game", "word_search"],
  culture: ["cultural_symbols", "music_instruments", "traditional_dance", "festival_lanterns", "language_bubbles", "food_world"],
  micro_biology: ["cell_division", "virus_structure", "bacteria_colony", "immune_response", "neuron_firing", "dna_replication", "protein_folding", "blood_cells"],
  character: ["walking_character", "running_character", "jumping_character", "waving_character", "sitting_reading", "pointing_teacher", "dancing_character", "swimming_character"],
  abstract: ["time_flow", "cause_effect", "growth_chart", "teamwork_gears", "leadership_pyramid", "innovation_spark", "life_cycle"],
  // Concept-acting scenes: prefer these — they ACT the idea out (relatable, kid-friendly)
  // instead of labelling boxes. Use for the matching concept.
  concept_scene: ["car_speed_scene", "speed_compare_scene", "gravity_scene", "states_of_matter_scene", "water_cycle_scene", "day_night_scene", "photosynthesis_scene", "food_chain_scene", "fractions_scene", "counting_scene", "addition_scene", "subtraction_scene", "breathing_scene", "electricity_scene", "seasons_scene", "butterfly_life_scene", "rainbow_scene", "volcano_scene", "shapes_scene", "telling_time_scene", "float_sink_scene", "magnet_scene", "plant_grow_scene", "frog_life_scene", "coding_sequence_scene", "coding_loop_scene", "coding_if_scene", "debugging_scene", "machine_learning_scene", "neural_network_scene", "algorithm_scene", "sorting_scene", "journey_scene", "xiaohei_idea_scene", "xiaohei_walk_scene", "skin_demo_scene", "skin_landscape_scene", "actor_gallery_scene", "skin_park_scene", "skin_counting_scene", "skin_water_cycle", "skin_day_night", "skin_butterfly_life", "skin_frog_life", "skin_photosynthesis", "skin_food_chain", "skin_gravity", "skin_seasons", "skin_title", "skin_recap", "skin_breathing", "skin_explainer"],
  general: ["box_flow", "branching_tube", "cylinder"],
};
